// utils/telemetryUrlScrub.ts
//
// Defence in depth for ORA-1089: the pages that read a URL secret drop it
// from the address bar, but a page view or an error can be recorded before
// they do. These hooks scrub every URL PostHog and Sentry send.
import type { Breadcrumb, Event } from '@sentry/react';
import type { CaptureResult } from 'posthog-js';
import {
  scrubStringsDeep,
  scrubUrlProperties,
  scrubUrlPropertiesDeep,
  scrubUrlSecrets,
  scrubUrlSecretsInText,
  URL_SECRET_PARAMS,
} from './urlSecrets';

/** PostHog's URL-bearing properties (event, `$set` and `$set_once`). */
const POSTHOG_URL_PROPERTIES: readonly string[] = [
  '$current_url',
  '$initial_current_url',
  '$referrer',
  '$initial_referrer',
  '$pathname',
  '$initial_pathname',
  '$prev_pageview_pathname',
  '$session_entry_url',
  '$session_entry_pathname',
  '$session_entry_referrer',
];

/**
 * PostHog `before_send`: the event with its URLs scrubbed, at any depth (web
 * vitals nest their own `$current_url` inside `$web_vitals_*_event`).
 */
export const scrubPosthogEvent = (
  event: CaptureResult | null
): CaptureResult | null => {
  if (!event) return event;
  scrubUrlPropertiesDeep(event.properties, POSTHOG_URL_PROPERTIES);
  scrubUrlPropertiesDeep(event.$set, POSTHOG_URL_PROPERTIES);
  scrubUrlPropertiesDeep(event.$set_once, POSTHOG_URL_PROPERTIES);
  return event;
};

/** Breadcrumb `data` keys that hold a URL (navigation, fetch, xhr). */
const BREADCRUMB_URL_KEYS: readonly string[] = ['url', 'from', 'to'];

/** Sentry `beforeBreadcrumb`: the breadcrumb with its URLs scrubbed. */
export const scrubSentryBreadcrumb = (breadcrumb: Breadcrumb): Breadcrumb => {
  scrubUrlProperties(breadcrumb.data, BREADCRUMB_URL_KEYS);
  return breadcrumb;
};

/** Sentry `beforeSend` / `beforeSendTransaction`: the request URL, its query
 * string, the referrer and a URL-named transaction, scrubbed. */
export const scrubSentryEvent = <T extends Event>(event: T): T => {
  const request = event.request;
  if (request) {
    if (request.url) request.url = scrubUrlSecrets(request.url);

    // The query string is sent apart from the URL.
    if (typeof request.query_string === 'string') {
      const params = new URLSearchParams(request.query_string);
      URL_SECRET_PARAMS.forEach((param) => params.delete(param));
      request.query_string = params.toString();
    } else if (request.query_string) {
      delete request.query_string;
    }

    scrubUrlProperties(request.headers, ['Referer', 'referer']);
  }

  if (event.transaction) {
    event.transaction = scrubUrlSecrets(event.transaction);
  }

  event.breadcrumbs?.forEach(scrubSentryBreadcrumb);

  // A transaction's spans (fetch, xhr, navigation) name their URLs.
  event.spans?.forEach((span) => {
    if (span.description) {
      span.description = scrubUrlSecretsInText(span.description);
    }
    scrubUrlProperties(span.data, SPAN_URL_KEYS);
  });

  scrubUrlProperties(event.contexts?.trace?.data, SPAN_URL_KEYS);

  // A replay event lists every URL the session visited.
  if (event.type === 'replay_event') {
    const replay = event as Event & { urls?: unknown };
    if (Array.isArray(replay.urls)) {
      replay.urls = replay.urls.map((url) =>
        typeof url === 'string' ? scrubUrlSecrets(url) : url
      );
    }
  }

  return event;
};

/** Span `data` keys that hold a URL. */
const SPAN_URL_KEYS: readonly string[] = ['url', 'http.url', 'url.full'];
/** Replay recording tags whose payload holds URLs (navigation, network). */
const REPLAY_URL_TAGS = new Set(['performanceSpan', 'breadcrumb']);
/** rrweb's page-load meta event, whose `href` is the page URL. */
const RRWEB_META_EVENT = 4;

/**
 * Sentry Replay `beforeAddRecordingEvent`: navigation and network spans,
 * breadcrumbs and the page-load URL, scrubbed.
 */
export const scrubReplayRecordingEvent = <T>(event: T): T => {
  const recording = event as {
    type?: unknown;
    data?: { tag?: unknown; payload?: unknown; href?: unknown };
  } | null;

  const data = recording?.data;
  if (!data || typeof data !== 'object') return event;

  if (recording?.type === RRWEB_META_EVENT && typeof data.href === 'string') {
    data.href = scrubUrlSecrets(data.href);
  }

  if (typeof data.tag === 'string' && REPLAY_URL_TAGS.has(data.tag)) {
    scrubStringsDeep(data.payload);
  }

  return event;
};
